/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: usersActions.ts
 🗓️ Created: 2026-05-02
 🧩 Description:
    Implementation of the Users module.
────────────────────────────────────────────────────────────────*/

import type { NavigateFunction } from 'react-router-dom';
import { buildApiUrl, getApiHeaders, API_CONFIG } from '../../config/api';

/**
 * Shared helpers for Users module actions (used in Grid + Form views).
 */

/**
 * Build the URL used to duplicate a User in the form.
 * Pattern: /users/new?duplicate={id}
 */
export const getUserDuplicateUrl = (id: string | number): string =>
  `/users/new?duplicate=${id}`;

/**
 * Navigate to the form in duplicate mode for the given User id.
 */
export const navigateToUserDuplicate = (
  navigate: NavigateFunction,
  id: string | number,
): void => {
  navigate(getUserDuplicateUrl(id));
};

/**
 * Low-level API helper to delete a User by id.
 * UI components are responsible for showing notifications and handling navigation/refresh.
 */
export const deleteUserById = async (
  id: string | number,
  tenantSlug?: string,
  userId?: string | number | null
): Promise<void> => {
  const response = await fetch(
    buildApiUrl(`${API_CONFIG.ENDPOINTS.USERS}/${id}`),
    {
      method: 'DELETE',
      headers: getApiHeaders(tenantSlug, userId),
    },
  );

  if (!response.ok) {
    let message = 'Failed to delete User';
    try {
      const errorData = await response.json();
      if (errorData?.message) {
        message = errorData.message;
      }
    } catch {
      // Ignore JSON parse errors and use default message
    }
    throw new Error(message);
  }
};

